import React, { useState, useEffect, useRef, Suspense } from "react";
import { Outlet } from "react-router-dom";
import Sidebar from "./Sidebar";
import MobileBottomNav from "./MobileBottomNav";
import useIsMobile from "../hooks/useIsMobile";
import { useSimulator } from "../contexts/SimulatorContext";
import { lazyWithRetry } from "../utils/lazyWithRetry";
import LeadEditModal from "./contacts/LeadEditModal";
import PendingDemosNudge from "./demos/PendingDemosNudge";

// El SIMULADOR entero (árbol de ~30 páginas mock, ~8MB de src) se carga lazy:
// SimulatorApp es la raíz que importa todas las páginas mock, así que separarlo
// aquí saca TODO ese árbol del bundle inicial. Solo se descarga cuando el
// vendedor activa el modo demo (isActive). SimulatorSectorSelect es la pantalla
// previa de selección de sector, también fuera del main.
const SimulatorApp = lazyWithRetry(() => import("../simulator/SimulatorApp"), "simulator-app");
const SimulatorSectorSelect = lazyWithRetry(() => import("../simulator/SimulatorSectorSelect"), "simulator-sector-select");

/** Spinner mientras se descarga el chunk del simulador (solo al activar demo). */
function SimulatorLoadingFallback() {
  return (
    <div className="flex h-full w-full items-center justify-center">
      <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-indigo-600" />
    </div>
  );
}

/**
 * Application layout with sidebar and main content area.
 * Provides vertical scrolling for pages that exceed viewport height.
 * @returns {JSX.Element} layout container
 */
const Layout = () => {
  const isMobile = useIsMobile();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { isActive, sector, returnLead, clearReturnLead } = useSimulator();

  // When simulator deactivates and there is a returnLead, reopen LeadEditModal
  const [pendingLead, setPendingLead] = useState(null);
  const prevIsActive = useRef(isActive);
  useEffect(() => {
    if (prevIsActive.current && !isActive && returnLead) {
      setPendingLead(returnLead);
      clearReturnLead();
    }
    prevIsActive.current = isActive;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  return (
    <div className="flex h-full overflow-hidden bg-gray-50 dark:bg-gray-900">
      {/* Sidebar solo cuando el simulador NO está activo */}
      {!isActive && !isMobile && <Sidebar />}
      {!isActive && isMobile && (
        <Sidebar
          isMobile={true}
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
        />
      )}

      {/* El hueco de la MobileBottomNav se reserva con PADDING del <main>, no con
          un spacer dentro: `main` es `box-sizing: border-box`, así que el padding
          recorta su caja de contenido y las páginas de altura fija (`h-full`,
          como Chats) miden justo lo visible por encima de la barra.
          Con un spacer, en cambio, el contenido del main sumaba 100% + 4rem y el
          propio main quedaba scrollable 64px: en iOS ese ancestro se llevaba el
          gesto y la lista de chats "no scrolleaba" — se movía la página entera. */}
      <main
        className="flex-grow overflow-y-auto h-full relative"
        style={
          !isActive && isMobile
            ? { paddingBottom: "calc(4rem + env(safe-area-inset-bottom))" }
            : undefined
        }
      >
        {/* Simulador (lazy): su chunk solo se descarga al activar el modo demo */}
        {isActive && (
          <Suspense fallback={<SimulatorLoadingFallback />}>
            {!sector ? <SimulatorSectorSelect /> : <SimulatorApp />}
          </Suspense>
        )}

        {/* App de ventas normal */}
        {!isActive && <Outlet />}
      </main>

      {!isActive && isMobile && (
        <MobileBottomNav onMenuClick={() => setIsSidebarOpen(true)} />
      )}

      {!isActive && <PendingDemosNudge />}

      {/* Reopen LeadEditModal after exiting simulator from a lead */}
      {pendingLead && (
        <LeadEditModal
          open={true}
          onClose={() => setPendingLead(null)}
          contactId={pendingLead.contactId}
          initialData={pendingLead.initialData}
          onSaved={() => {
            // No cerramos el modal al guardar; el usuario cierra con la X.
          }}
        />
      )}
    </div>
  );
};

export default Layout;
